import { useEffect, useMemo, useRef, useState } from 'react'
import {
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    CheckCircle2,
    Download,
    Loader2,
    MapPin,
    Plus,
    RefreshCw,
    Save,
    Upload,
    XCircle,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { trpc } from '@/lib/trpc'
import type {
    City,
    ImportPincodeMappingsResult,
    ManageGeoCluster,
    MappingPreviewAction,
    PincodeClusterApplyPreviewResponse,
    PincodeClusterMapping,
    PincodeClusterMappingListResponse,
} from '@/types'

interface FeedbackState {
    type: 'success' | 'error'
    message: string
}

type EditableScope = 'city' | 'global'
type PreviewActionFilter = 'all' | MappingPreviewAction
const MAPPING_PAGE_SIZE_OPTIONS = [25, 50, 100]
const PREVIEW_PAGE_SIZE_OPTIONS = [30, 50, 100]
const MAPPING_EXPORT_PAGE_SIZE = 200

function getTotalPages(total: number, pageSize: number): number {
    return Math.max(1, Math.ceil(total / pageSize))
}

function getPageRange(page: number, pageSize: number, total: number): string {
    if (total === 0) return '0-0'
    const start = (page - 1) * pageSize + 1
    const end = Math.min(page * pageSize, total)
    return `${start}-${end}`
}

function formatDate(value?: string | null): string {
    if (!value) return '-'
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value))
}

function downloadCsvTemplate(): void {
    const template = 'city_id,pincode,cluster_id,is_active\n4,110001,1,true\n4,110048,2,true\n'
    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'pincode_cluster_mappings_template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

function sanitizePincodeInput(value: string): string {
    return value.replace(/\D/g, '').slice(0, 6)
}

function getActionBadgeVariant(action: MappingPreviewAction): 'default' | 'outline' | 'destructive' {
    if (action === 'update') return 'default'
    if (action === 'unmapped') return 'destructive'
    return 'outline'
}

function escapeCsvValue(value: string | number | boolean | null | undefined): string {
    if (value === null || value === undefined) return ''
    const normalized = String(value).replace(/"/g, '""')
    return `"${normalized}"`
}

export function GeoMappingsSettingsPage() {
    const { currentWorkspace, isAdmin } = useWorkspace()
    const workspaceId = currentWorkspace?.id

    // tRPC utils for imperative query calls + mutations for write operations
    const utils = trpc.useUtils()
    const setMappingActiveMutation = trpc.store.setPincodeMappingActive.useMutation()
    const upsertMappingsMutation = trpc.store.upsertPincodeMappings.useMutation()
    const importCsvMutation = trpc.store.importPincodeMappingsCsv.useMutation()
    const createClusterMutation = trpc.store.createCluster.useMutation()
    const updateClusterMutation = trpc.store.updateCluster.useMutation()
    const deactivateClusterMutation = trpc.store.deactivateCluster.useMutation()
    const applyMappingsMutation = trpc.store.applyMappings.useMutation()

    const [cities, setCities] = useState<City[]>([])
    const [clusters, setClusters] = useState<ManageGeoCluster[]>([])
    const [mappings, setMappings] = useState<PincodeClusterMappingListResponse | null>(null)
    const [preview, setPreview] = useState<PincodeClusterApplyPreviewResponse | null>(null)

    const [selectedCityId, setSelectedCityId] = useState<string>('all')
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [previewScope, setPreviewScope] = useState<EditableScope>('city')
    const [previewActionFilter, setPreviewActionFilter] = useState<PreviewActionFilter>('update')
    const [mappingPage, setMappingPage] = useState(1)
    const [mappingPageSize, setMappingPageSize] = useState(MAPPING_PAGE_SIZE_OPTIONS[1])
    const [previewPage, setPreviewPage] = useState(1)
    const [previewPageSize, setPreviewPageSize] = useState(PREVIEW_PAGE_SIZE_OPTIONS[0])

    const [isLoadingPage, setIsLoadingPage] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isExportingMappings, setIsExportingMappings] = useState(false)
    const [feedback, setFeedback] = useState<FeedbackState | null>(null)
    const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
    const loadVersionRef = useRef(0)

    const [editingMappingId, setEditingMappingId] = useState<number | null>(null)
    const [editingClusterId, setEditingClusterId] = useState<string>('')

    const [isAddMappingDialogOpen, setIsAddMappingDialogOpen] = useState(false)
    const [newMappingCityId, setNewMappingCityId] = useState<string>('')
    const [newMappingPincode, setNewMappingPincode] = useState('')
    const [newMappingClusterId, setNewMappingClusterId] = useState('')
    const [newMappingIsActive, setNewMappingIsActive] = useState(true)

    const [csvFile, setCsvFile] = useState<File | null>(null)
    const [csvResult, setCsvResult] = useState<ImportPincodeMappingsResult | null>(null)

    const [newClusterName, setNewClusterName] = useState('')
    const [newClusterCode, setNewClusterCode] = useState('')
    const [editingClusterRowId, setEditingClusterRowId] = useState<number | null>(null)
    const [editingClusterName, setEditingClusterName] = useState('')
    const [editingClusterCode, setEditingClusterCode] = useState('')

    const [applyDialog, setApplyDialog] = useState<{ open: boolean; scope: EditableScope }>({
        open: false,
        scope: 'city',
    })

    const cityId = selectedCityId !== 'all' ? Number(selectedCityId) : undefined

    const clustersByCity = useMemo(() => {
        const map = new Map<number, ManageGeoCluster[]>()
        for (const cluster of clusters) {
            const existing = map.get(cluster.cityId) || []
            existing.push(cluster)
            map.set(cluster.cityId, existing)
        }
        for (const [key, value] of map.entries()) {
            map.set(
                key,
                [...value].sort((a, b) => a.name.localeCompare(b.name))
            )
        }
        return map
    }, [clusters])

    const cityClusters = useMemo(() => {
        if (!cityId) return []
        return clustersByCity.get(cityId) || []
    }, [clustersByCity, cityId])

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(searchInput.trim())
        }, 300)
        return () => window.clearTimeout(timer)
    }, [searchInput])

    useEffect(() => {
        setNewMappingClusterId('')
    }, [newMappingCityId])

    useEffect(() => {
        setMappingPage(1)
    }, [selectedCityId, search])

    useEffect(() => {
        setPreviewPage(1)
    }, [previewScope, previewActionFilter, selectedCityId])

    const mappingOffset = (mappingPage - 1) * mappingPageSize
    const previewOffset = (previewPage - 1) * previewPageSize

    const loadPageData = async (showSpinner: boolean = false) => {
        if (!workspaceId) return

        const loadVersion = ++loadVersionRef.current

        if (showSpinner) {
            setIsRefreshing(true)
        } else {
            setIsLoadingPage(true)
        }

        try {
            const [citiesResult, clustersData, mappingsData] = await Promise.all([
                utils.store.cities.fetch({}),
                utils.store.listClusters.fetch({ workspaceId, includeInactive: true }),
                utils.store.pincodeMappings.fetch({
                    workspaceId,
                    cityId,
                    search: search.trim() || undefined,
                    limit: mappingPageSize,
                    offset: mappingOffset,
                }),
            ])

            if (loadVersion !== loadVersionRef.current) return

            if (mappingsData) {
                const totalMappingPages = getTotalPages(mappingsData.total, mappingPageSize)
                if (mappingPage > totalMappingPages) {
                    setMappingPage(totalMappingPages)
                    return
                }
            }

            setCities(citiesResult.cities)
            setClusters(clustersData.clusters)
            setMappings(mappingsData)

            const effectiveScope: EditableScope = previewScope
            if (previewScope === 'city' && !cityId) {
                setPreview({
                    rows: [],
                    total: 0,
                    limit: previewPageSize,
                    offset: previewOffset,
                    actionCounts: {
                        update: 0,
                        unchanged: 0,
                        unmapped: 0,
                        mapped: 0,
                        pendingUpdates: 0,
                    },
                })
                setLastUpdatedAt(new Date())
                return
            }

            const previewData = await utils.store.previewApplyMappings.fetch({
                workspaceId,
                scope: effectiveScope,
                cityId: effectiveScope === 'city' ? cityId : undefined,
                action: previewActionFilter === 'all' ? undefined : previewActionFilter,
                limit: previewPageSize,
                offset: previewOffset,
            })
            if (loadVersion !== loadVersionRef.current) return

            if (previewData) {
                const totalPreviewPages = getTotalPages(previewData.total, previewPageSize)
                if (previewPage > totalPreviewPages) {
                    setPreviewPage(totalPreviewPages)
                    return
                }
            }

            setPreview(previewData)
            setLastUpdatedAt(new Date())
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to load geo mappings settings',
            })
        } finally {
            setIsLoadingPage(false)
            setIsRefreshing(false)
        }
    }

    useEffect(() => {
        void loadPageData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        workspaceId,
        selectedCityId,
        search,
        previewScope,
        previewActionFilter,
        mappingPage,
        mappingPageSize,
        previewPage,
        previewPageSize,
    ])

    const handleRefresh = async () => {
        await loadPageData(true)
    }

    const handleToggleMappingStatus = async (row: PincodeClusterMapping, nextValue: boolean) => {
        if (!workspaceId || !isAdmin) return

        setIsSaving(true)
        setFeedback(null)

        try {
            await setMappingActiveMutation.mutateAsync({ workspaceId, id: row.id, isActive: nextValue })
            setFeedback({ type: 'success', message: 'Mapping status updated' })
            await loadPageData(true)
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to update mapping status',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const beginEditMapping = (row: PincodeClusterMapping) => {
        setEditingMappingId(row.id)
        setEditingClusterId(String(row.clusterId))
    }

    const saveEditedMapping = async (row: PincodeClusterMapping) => {
        if (!workspaceId || !isAdmin || !editingClusterId) return

        setIsSaving(true)
        setFeedback(null)

        try {
            await upsertMappingsMutation.mutateAsync({
                workspaceId,
                rows: [
                    {
                        cityId: row.cityId,
                        pincode: row.pincode,
                        clusterId: Number(editingClusterId),
                        isActive: row.isActive,
                    },
                ],
            })
            setFeedback({ type: 'success', message: 'Mapping updated' })
            setEditingMappingId(null)
            setEditingClusterId('')
            await loadPageData(true)
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to update mapping',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const handleAddMapping = async () => {
        if (!workspaceId || !isAdmin) return

        if (!newMappingCityId || !newMappingPincode || !newMappingClusterId) {
            setFeedback({
                type: 'error',
                message: 'City, pincode, and cluster are required to create a mapping',
            })
            return
        }
        if (newMappingPincode.length !== 6) {
            setFeedback({
                type: 'error',
                message: 'Pincode must be exactly 6 digits',
            })
            return
        }

        setIsSaving(true)
        setFeedback(null)

        try {
            await upsertMappingsMutation.mutateAsync({
                workspaceId,
                rows: [
                    {
                        cityId: Number(newMappingCityId),
                        pincode: sanitizePincodeInput(newMappingPincode),
                        clusterId: Number(newMappingClusterId),
                        isActive: newMappingIsActive,
                    },
                ],
            })

            setFeedback({ type: 'success', message: 'Mapping created successfully' })
            setIsAddMappingDialogOpen(false)
            setNewMappingCityId('')
            setNewMappingPincode('')
            setNewMappingClusterId('')
            setNewMappingIsActive(true)
            await loadPageData(true)
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to create mapping',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const handleImportCsv = async () => {
        if (!workspaceId || !isAdmin || !csvFile) return

        setIsSaving(true)
        setFeedback(null)
        setCsvResult(null)

        try {
            // Parse CSV client-side, then send rows to tRPC
            const text = await csvFile.text()
            const lines = text.trim().split('\n')
            const header = lines[0]?.toLowerCase().trim() ?? ''
            const hasHeader = header.includes('city_id') || header.includes('pincode')
            const dataLines = hasHeader ? lines.slice(1) : lines
            const parsedRows: Array<{ cityId: number; pincode: string; clusterId: number; isActive?: boolean }> = []
            const parseErrors: Array<{ rowNumber: number; field: string; message: string }> = []

            dataLines.forEach((line, idx) => {
                const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''))
                if (cols.length < 3) {
                    parseErrors.push({ rowNumber: idx + (hasHeader ? 2 : 1), field: 'row', message: 'Insufficient columns' })
                    return
                }
                const cityId = Number(cols[0])
                const pincode = cols[1]
                const clusterId = Number(cols[2])
                const isActive = cols[3] !== undefined ? cols[3].toLowerCase() !== 'false' : true

                if (isNaN(cityId) || cityId <= 0) {
                    parseErrors.push({ rowNumber: idx + (hasHeader ? 2 : 1), field: 'city_id', message: 'Invalid city_id' })
                    return
                }
                if (!pincode || !/^\d{6}$/.test(pincode)) {
                    parseErrors.push({ rowNumber: idx + (hasHeader ? 2 : 1), field: 'pincode', message: 'Pincode must be 6 digits' })
                    return
                }
                if (isNaN(clusterId) || clusterId <= 0) {
                    parseErrors.push({ rowNumber: idx + (hasHeader ? 2 : 1), field: 'cluster_id', message: 'Invalid cluster_id' })
                    return
                }
                parsedRows.push({ cityId, pincode, clusterId, isActive })
            })

            if (parseErrors.length > 0) {
                setCsvResult({ totalRows: dataLines.length, importedRows: 0, errors: parseErrors })
                setFeedback({ type: 'error', message: `CSV parse failed with ${parseErrors.length} error(s)` })
                setIsSaving(false)
                return
            }

            if (parsedRows.length === 0) {
                setFeedback({ type: 'error', message: 'CSV file contains no valid rows' })
                setIsSaving(false)
                return
            }

            const result = await importCsvMutation.mutateAsync({ workspaceId, rows: parsedRows })
            setCsvResult(result)

            if (result.errors.length > 0) {
                setFeedback({
                    type: 'error',
                    message: `CSV import failed with ${result.errors.length} validation error(s)`,
                })
            } else {
                setFeedback({
                    type: 'success',
                    message: `Imported ${result.importedRows} mappings from CSV`,
                })
                setCsvFile(null)
                await loadPageData(true)
            }
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'CSV import failed',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const handleCreateCluster = async () => {
        if (!workspaceId || !isAdmin || !cityId) return

        if (!newClusterName.trim()) {
            setFeedback({ type: 'error', message: 'Cluster name is required' })
            return
        }

        setIsSaving(true)
        setFeedback(null)

        try {
            await createClusterMutation.mutateAsync({
                workspaceId,
                cityId: cityId!,
                name: newClusterName.trim(),
                code: newClusterCode.trim() || undefined,
            })

            setNewClusterName('')
            setNewClusterCode('')
            setFeedback({ type: 'success', message: 'Cluster created' })
            await loadPageData(true)
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to create cluster',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const beginEditCluster = (cluster: ManageGeoCluster) => {
        setEditingClusterRowId(cluster.id)
        setEditingClusterName(cluster.name)
        setEditingClusterCode(cluster.code)
    }

    const saveClusterEdit = async (cluster: ManageGeoCluster) => {
        if (!workspaceId || !isAdmin) return

        const trimmedName = editingClusterName.trim()
        const trimmedCode = editingClusterCode.trim()
        const payload: { name?: string; code?: string } = {}

        if (trimmedName !== cluster.name) payload.name = trimmedName
        if (trimmedCode && trimmedCode !== cluster.code) payload.code = trimmedCode

        if (!payload.name && !payload.code) {
            setEditingClusterRowId(null)
            setEditingClusterName('')
            setEditingClusterCode('')
            return
        }

        setIsSaving(true)
        setFeedback(null)

        try {
            await updateClusterMutation.mutateAsync({ workspaceId, clusterId: cluster.id, ...payload })

            setEditingClusterRowId(null)
            setEditingClusterName('')
            setEditingClusterCode('')
            setFeedback({ type: 'success', message: 'Cluster updated' })
            await loadPageData(true)
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to update cluster',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const handleDeactivateCluster = async (cluster: ManageGeoCluster) => {
        if (!workspaceId || !isAdmin) return

        const confirmed = window.confirm(
            `Deactivate cluster '${cluster.name}'? This does not delete historical data.`
        )
        if (!confirmed) return

        setIsSaving(true)
        setFeedback(null)

        try {
            await deactivateClusterMutation.mutateAsync({ workspaceId, clusterId: cluster.id })
            setFeedback({ type: 'success', message: 'Cluster deactivated' })
            await loadPageData(true)
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to deactivate cluster',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const openApplyDialog = (scope: EditableScope) => {
        if (!workspaceId || !isAdmin) return
        if (scope === 'city' && !cityId) {
            setFeedback({ type: 'error', message: 'Select a city before running city-scoped apply' })
            return
        }
        setApplyDialog({ open: true, scope })
    }

    const handleApplyMappings = async () => {
        if (!workspaceId || !isAdmin) return

        const scope = applyDialog.scope
        if (scope === 'city' && !cityId) return

        setIsSaving(true)
        setFeedback(null)

        try {
            const result = await applyMappingsMutation.mutateAsync({
                workspaceId,
                scope,
                cityId: scope === 'city' ? cityId : undefined,
            })

            setFeedback({
                type: 'success',
                message: `Apply completed: ${result.updatedCount} stores updated, ${result.unmappedCount} unmatched stores left unchanged`,
            })
            setApplyDialog({ open: false, scope: 'city' })
            await loadPageData(true)
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to apply mappings',
            })
        } finally {
            setIsSaving(false)
        }
    }

    const handleDownloadAllMappings = async () => {
        if (!workspaceId) return

        setIsExportingMappings(true)
        setFeedback(null)

        try {
            const allRows: PincodeClusterMapping[] = []
            let offset = 0
            let total = 0

            while (true) {
                const page = await utils.store.pincodeMappings.fetch({
                    workspaceId,
                    limit: MAPPING_EXPORT_PAGE_SIZE,
                    offset,
                })

                if (!page) {
                    throw new Error('Failed to fetch mappings for export')
                }

                allRows.push(...page.rows)
                total = page.total

                if (page.rows.length === 0 || allRows.length >= total) {
                    break
                }

                offset += page.rows.length
            }

            const header = [
                'mapping_id',
                'city_id',
                'city_name',
                'pincode',
                'cluster_id',
                'cluster_name',
                'is_active',
                'matched_store_count',
                'changed_store_count',
                'created_at',
                'updated_at',
            ]

            const lines = [
                header.join(','),
                ...allRows.map((row) =>
                    [
                        row.id,
                        row.cityId,
                        row.cityName,
                        row.pincode,
                        row.clusterId,
                        row.clusterName,
                        row.isActive,
                        row.matchedStoreCount,
                        row.changedStoreCount,
                        row.createdAt,
                        row.updatedAt,
                    ]
                        .map((value) => escapeCsvValue(value))
                        .join(',')
                ),
            ]

            const csvContent = lines.join('\n')
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `pincode_cluster_mappings_${timestamp}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)

            setFeedback({
                type: 'success',
                message: `Downloaded ${allRows.length} mapping(s) as CSV`,
            })
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to download mappings CSV',
            })
        } finally {
            setIsExportingMappings(false)
        }
    }

    const clearFilters = () => {
        setSelectedCityId('all')
        setSearchInput('')
        setSearch('')
        setPreviewScope('global')
        setPreviewActionFilter('update')
        setMappingPage(1)
        setPreviewPage(1)
    }

    const mappingTotal = mappings?.total ?? 0
    const mappingPages = getTotalPages(mappingTotal, mappingPageSize)
    const previewTotal = preview?.total ?? 0
    const previewPages = getTotalPages(previewTotal, previewPageSize)

    if (isLoadingPage) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    return (
        <div className="p-6 md:p-8 w-full animate-fade-in">
            <div className="max-w-[1200px] mx-auto space-y-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <MapPin className="h-6 w-6 text-primary" />
                            <h1 className="app-page-title">Pincode to Cluster Mapping</h1>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            Manage canonical pincode mappings and preview store-level cluster updates before applying.
                        </p>
                        {!isAdmin && (
                            <Badge variant="outline" className="mt-2 text-xs">
                                Read-only mode (workspace member)
                            </Badge>
                        )}
                        {lastUpdatedAt && (
                            <p className="mt-2 text-xs text-muted-foreground">
                                Last refreshed: {new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(lastUpdatedAt)}
                            </p>
                        )}
                    </div>
                    <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing || isSaving}>
                        {isRefreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Refresh
                    </Button>
                </div>

                {feedback && (
                    <Card className={feedback.type === 'success' ? 'border-green-500/40 bg-green-500/5' : 'border-destructive/40 bg-destructive/5'}>
                        <CardContent className="p-3 flex items-start gap-2">
                            {feedback.type === 'success' ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                            ) : (
                                <XCircle className="h-4 w-4 text-destructive mt-0.5" />
                            )}
                            <p className="text-sm">{feedback.message}</p>
                        </CardContent>
                    </Card>
                )}

                {mappings?.mappingStats.activeMappings === 0 && (
                    <Card className="border-amber-500/40 bg-amber-500/5">
                        <CardContent className="p-3 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                            <p className="text-sm text-amber-900 dark:text-amber-100">
                                No active mappings found. Applying now would not assign unmatched stores; load mappings first.
                            </p>
                        </CardContent>
                    </Card>
                )}

                <Tabs defaultValue="overview" className="space-y-4">
                    <TabsList className="h-auto w-full justify-start gap-1 flex-wrap p-1">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="mappings">Mappings</TabsTrigger>
                        <TabsTrigger value="import">CSV Import</TabsTrigger>
                        <TabsTrigger value="clusters">Clusters</TabsTrigger>
                        <TabsTrigger value="preview">Preview & Apply</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-4">
                        <div className="flex justify-end">
                            <Button
                                variant="outline"
                                onClick={() => void handleDownloadAllMappings()}
                                disabled={isRefreshing || isSaving || isExportingMappings}
                            >
                                {isExportingMappings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                                Download All Mappings (CSV)
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardDescription>Active mappings</CardDescription>
                                    <CardTitle className="text-2xl">{mappings?.mappingStats.activeMappings ?? 0}</CardTitle>
                                </CardHeader>
                            </Card>
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardDescription>Mapped active stores</CardDescription>
                                    <CardTitle className="text-2xl">{preview?.actionCounts.mapped ?? 0}</CardTitle>
                                </CardHeader>
                            </Card>
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardDescription>Unmapped active stores</CardDescription>
                                    <CardTitle className="text-2xl">{preview?.actionCounts.unmapped ?? 0}</CardTitle>
                                </CardHeader>
                            </Card>
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardDescription>Pending updates ({previewScope})</CardDescription>
                                    <CardTitle className="text-2xl">{preview?.actionCounts.pendingUpdates ?? 0}</CardTitle>
                                </CardHeader>
                            </Card>
                        </div>

                        <Card>
                            <CardHeader>
                                <CardTitle>Scope and Filters</CardTitle>
                                <CardDescription>Select city scope, search mappings, and preview update actions.</CardDescription>
                            </CardHeader>
                            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="space-y-1">
                                    <Label>City</Label>
                                    <Select value={selectedCityId} onValueChange={setSelectedCityId}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="All cities" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All cities</SelectItem>
                                            {cities.map((city) => (
                                                <SelectItem key={city.id} value={String(city.id)}>
                                                    {city.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label>Search PIN</Label>
                                    <Input
                                        value={searchInput}
                                        onChange={(event) => setSearchInput(sanitizePincodeInput(event.target.value))}
                                        placeholder="e.g. 560001"
                                        inputMode="numeric"
                                        maxLength={6}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Preview scope</Label>
                                    <Select value={previewScope} onValueChange={(value) => setPreviewScope(value as EditableScope)}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="city">City scope</SelectItem>
                                            <SelectItem value="global">Global scope</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label>Preview action filter</Label>
                                    <Select value={previewActionFilter} onValueChange={(value) => setPreviewActionFilter(value as 'all' | MappingPreviewAction)}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All actions</SelectItem>
                                            <SelectItem value="update">Update</SelectItem>
                                            <SelectItem value="unchanged">Unchanged</SelectItem>
                                            <SelectItem value="unmapped">Unmapped</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="md:col-span-4 flex flex-wrap justify-end">
                                    <Button variant="ghost" onClick={clearFilters} disabled={isSaving || isRefreshing}>
                                        Reset filters
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="mappings">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0">
                                <div>
                                    <CardTitle>Mappings</CardTitle>
                                    <CardDescription>
                                        Inline edit pincode mappings and see store impact counts quickly.
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline">
                                        Showing {getPageRange(mappingPage, mappingPageSize, mappingTotal)} of {mappingTotal}
                                    </Badge>
                                    <Button onClick={() => setIsAddMappingDialogOpen(true)} disabled={!isAdmin || isSaving}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Mapping
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>City</TableHead>
                                                <TableHead>Pincode</TableHead>
                                                <TableHead>Cluster</TableHead>
                                                <TableHead>Active</TableHead>
                                                <TableHead className="text-right">Matched Stores</TableHead>
                                                <TableHead className="text-right">Changed Stores</TableHead>
                                                <TableHead>Updated</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(mappings?.rows || []).map((row) => {
                                                const options = (clustersByCity.get(row.cityId) || []).filter((cluster) => cluster.isActive)
                                                const isEditing = editingMappingId === row.id

                                                return (
                                                    <TableRow key={row.id} className={!row.isActive ? 'opacity-60' : undefined}>
                                                        <TableCell>{row.cityName}</TableCell>
                                                        <TableCell className="font-mono">{row.pincode}</TableCell>
                                                        <TableCell>
                                                            {isEditing ? (
                                                                <Select value={editingClusterId} onValueChange={setEditingClusterId}>
                                                                    <SelectTrigger className="max-w-[230px]">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {options.map((cluster) => (
                                                                            <SelectItem key={cluster.id} value={String(cluster.id)}>
                                                                                {cluster.name}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            ) : (
                                                                row.clusterName
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Switch
                                                                checked={row.isActive}
                                                                disabled={!isAdmin || isSaving}
                                                                onCheckedChange={(value) => void handleToggleMappingStatus(row, value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="text-right">{row.matchedStoreCount}</TableCell>
                                                        <TableCell className="text-right">{row.changedStoreCount}</TableCell>
                                                        <TableCell>{formatDate(row.updatedAt)}</TableCell>
                                                        <TableCell className="text-right">
                                                            {isEditing ? (
                                                                <div className="flex justify-end gap-2">
                                                                    <Button size="sm" onClick={() => void saveEditedMapping(row)} disabled={isSaving || !editingClusterId}>
                                                                        <Save className="h-3.5 w-3.5 mr-1" />
                                                                        Save
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => {
                                                                            setEditingMappingId(null)
                                                                            setEditingClusterId('')
                                                                        }}
                                                                        disabled={isSaving}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <Button size="sm" variant="outline" onClick={() => beginEditMapping(row)} disabled={!isAdmin || isSaving}>
                                                                    Edit
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                            {(mappings?.rows.length || 0) === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                                        No mappings found for the current filters.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs text-muted-foreground">Rows per page</Label>
                                        <Select
                                            value={String(mappingPageSize)}
                                            onValueChange={(value) => {
                                                setMappingPageSize(Number(value))
                                                setMappingPage(1)
                                            }}
                                        >
                                            <SelectTrigger className="w-[88px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {MAPPING_PAGE_SIZE_OPTIONS.map((size) => (
                                                    <SelectItem key={size} value={String(size)}>
                                                        {size}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setMappingPage((prev) => Math.max(1, prev - 1))}
                                            disabled={mappingPage <= 1 || isRefreshing || isSaving}
                                        >
                                            <ChevronLeft className="h-4 w-4 mr-1" />
                                            Prev
                                        </Button>
                                        <p className="text-sm text-muted-foreground min-w-[96px] text-center">
                                            Page {mappingPage} of {mappingPages}
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setMappingPage((prev) => Math.min(mappingPages, prev + 1))}
                                            disabled={mappingPage >= mappingPages || isRefreshing || isSaving}
                                        >
                                            Next
                                            <ChevronRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="import">
                        <Card>
                            <CardHeader>
                                <CardTitle>CSV Import</CardTitle>
                                <CardDescription>
                                    Bulk import mappings with columns: <span className="font-mono">city_id,pincode,cluster_id,is_active</span>
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button variant="outline" onClick={downloadCsvTemplate}>
                                        Download Template
                                    </Button>
                                    <Input
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={(event) => setCsvFile(event.target.files?.[0] || null)}
                                        className="max-w-sm"
                                    />
                                    <Button onClick={() => void handleImportCsv()} disabled={!csvFile || !isAdmin || isSaving}>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Import CSV
                                    </Button>
                                    {csvFile && <Badge variant="secondary">{csvFile.name}</Badge>}
                                </div>

                                {csvResult && (
                                    <div className="rounded-md border p-3">
                                        <p className="text-sm font-medium">
                                            Parsed rows: {csvResult.totalRows} | Imported: {csvResult.importedRows} | Errors: {csvResult.errors.length}
                                        </p>
                                        {csvResult.errors.length > 0 && (
                                            <div className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">
                                                {csvResult.errors.map((error, index) => (
                                                    <div key={`${error.rowNumber}-${error.field}-${index}`} className="text-destructive">
                                                        Row {error.rowNumber} ({error.field}): {error.message}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="clusters">
                        <Card>
                            <CardHeader>
                                <CardTitle>Cluster Management</CardTitle>
                                <CardDescription>Create, edit, and deactivate geo clusters for the selected city.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {!cityId && (
                                    <p className="text-sm text-muted-foreground">Select a city to manage clusters.</p>
                                )}

                                {cityId && (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                            <Input
                                                placeholder="Cluster name"
                                                value={newClusterName}
                                                onChange={(event) => setNewClusterName(event.target.value)}
                                            />
                                            <Input
                                                placeholder="Cluster code (optional)"
                                                value={newClusterCode}
                                                onChange={(event) => setNewClusterCode(event.target.value)}
                                            />
                                            <Button onClick={() => void handleCreateCluster()} disabled={!isAdmin || isSaving}>
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add Cluster
                                            </Button>
                                        </div>

                                        <div className="rounded-md border overflow-hidden">
                                            <div className="overflow-x-auto">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Name</TableHead>
                                                            <TableHead>Code</TableHead>
                                                            <TableHead className="text-right">Active Mappings</TableHead>
                                                            <TableHead className="text-right">Active Stores</TableHead>
                                                            <TableHead className="text-right">Actions</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {cityClusters.map((cluster) => {
                                                            const isEditing = editingClusterRowId === cluster.id

                                                            return (
                                                                <TableRow key={cluster.id}>
                                                                    <TableCell>
                                                                        {isEditing ? (
                                                                            <Input value={editingClusterName} onChange={(event) => setEditingClusterName(event.target.value)} />
                                                                        ) : (
                                                                            cluster.name
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {isEditing ? (
                                                                            <Input value={editingClusterCode} onChange={(event) => setEditingClusterCode(event.target.value)} />
                                                                        ) : (
                                                                            <span className="font-mono text-xs">{cluster.code}</span>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">{cluster.activeMappingCount}</TableCell>
                                                                    <TableCell className="text-right">{cluster.activeStoreCount}</TableCell>
                                                                    <TableCell className="text-right">
                                                                        {isEditing ? (
                                                                            <div className="flex justify-end gap-2">
                                                                                <Button size="sm" onClick={() => void saveClusterEdit(cluster)} disabled={isSaving}>
                                                                                    Save
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    onClick={() => {
                                                                                        setEditingClusterRowId(null)
                                                                                        setEditingClusterName('')
                                                                                        setEditingClusterCode('')
                                                                                    }}
                                                                                    disabled={isSaving}
                                                                                >
                                                                                    Cancel
                                                                                </Button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex justify-end gap-2">
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    onClick={() => beginEditCluster(cluster)}
                                                                                    disabled={!isAdmin || isSaving || !cluster.isActive}
                                                                                >
                                                                                    Edit
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="destructive"
                                                                                    onClick={() => void handleDeactivateCluster(cluster)}
                                                                                    disabled={!isAdmin || isSaving || !cluster.isActive}
                                                                                >
                                                                                    Deactivate
                                                                                </Button>
                                                                            </div>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        })}
                                                        {cityClusters.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                                                    No clusters found for this city.
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="preview">
                        <Card>
                            <CardHeader>
                                <CardTitle>Preview and Apply</CardTitle>
                                <CardDescription>
                                    Preview impacted stores and apply mappings safely. Unmapped stores are never auto-unassigned.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <div className="rounded-md border p-3">
                                        <p className="text-xs text-muted-foreground">Update</p>
                                        <p className="text-lg font-semibold">{preview?.actionCounts.update ?? 0}</p>
                                    </div>
                                    <div className="rounded-md border p-3">
                                        <p className="text-xs text-muted-foreground">Unchanged</p>
                                        <p className="text-lg font-semibold">{preview?.actionCounts.unchanged ?? 0}</p>
                                    </div>
                                    <div className="rounded-md border p-3">
                                        <p className="text-xs text-muted-foreground">Unmapped</p>
                                        <p className="text-lg font-semibold">{preview?.actionCounts.unmapped ?? 0}</p>
                                    </div>
                                    <div className="rounded-md border p-3">
                                        <p className="text-xs text-muted-foreground">Mapped</p>
                                        <p className="text-lg font-semibold">{preview?.actionCounts.mapped ?? 0}</p>
                                    </div>
                                    <div className="rounded-md border p-3">
                                        <p className="text-xs text-muted-foreground">Pending updates</p>
                                        <p className="text-lg font-semibold">{preview?.actionCounts.pendingUpdates ?? 0}</p>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <Button onClick={() => openApplyDialog('city')} disabled={!isAdmin || !cityId || isSaving}>
                                        City Apply
                                    </Button>
                                    <Button variant="destructive" onClick={() => openApplyDialog('global')} disabled={!isAdmin || isSaving}>
                                        Global Apply
                                    </Button>
                                </div>

                                <div className="flex items-center justify-between gap-2">
                                    <Badge variant="outline">
                                        Showing {getPageRange(previewPage, previewPageSize, previewTotal)} of {previewTotal}
                                    </Badge>
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs text-muted-foreground">Rows per page</Label>
                                        <Select
                                            value={String(previewPageSize)}
                                            onValueChange={(value) => {
                                                setPreviewPageSize(Number(value))
                                                setPreviewPage(1)
                                            }}
                                        >
                                            <SelectTrigger className="w-[88px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {PREVIEW_PAGE_SIZE_OPTIONS.map((size) => (
                                                    <SelectItem key={size} value={String(size)}>
                                                        {size}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="rounded-md border overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Store</TableHead>
                                                <TableHead>City</TableHead>
                                                <TableHead>Pincode</TableHead>
                                                <TableHead>Current Cluster</TableHead>
                                                <TableHead>Mapped Cluster</TableHead>
                                                <TableHead>Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(preview?.rows || []).map((row) => (
                                                <TableRow key={row.storeId} className={row.action === 'update' ? 'bg-primary/5' : row.action === 'unmapped' ? 'bg-destructive/5' : undefined}>
                                                    <TableCell>{row.storeName}</TableCell>
                                                    <TableCell>{row.cityName}</TableCell>
                                                    <TableCell className="font-mono">{row.pincode || '-'}</TableCell>
                                                    <TableCell>{row.currentClusterName || '-'}</TableCell>
                                                    <TableCell>{row.mappedClusterName || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge variant={getActionBadgeVariant(row.action)}>
                                                            {row.action}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(preview?.rows.length || 0) === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                                                        No preview rows found for current scope and filters.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPreviewPage((prev) => Math.max(1, prev - 1))}
                                        disabled={previewPage <= 1 || isRefreshing || isSaving}
                                    >
                                        <ChevronLeft className="h-4 w-4 mr-1" />
                                        Prev
                                    </Button>
                                    <p className="text-sm text-muted-foreground min-w-[96px] text-center">
                                        Page {previewPage} of {previewPages}
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPreviewPage((prev) => Math.min(previewPages, prev + 1))}
                                        disabled={previewPage >= previewPages || isRefreshing || isSaving}
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            <Dialog open={isAddMappingDialogOpen} onOpenChange={setIsAddMappingDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Mapping</DialogTitle>
                        <DialogDescription>Create or update a city+pincode mapping.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <Label>City</Label>
                            <Select value={newMappingCityId} onValueChange={setNewMappingCityId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select city" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cities.map((city) => (
                                        <SelectItem key={city.id} value={String(city.id)}>
                                            {city.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label>Pincode</Label>
                            <Input
                                value={newMappingPincode}
                                onChange={(event) => setNewMappingPincode(sanitizePincodeInput(event.target.value))}
                                placeholder="6 digit pincode"
                                inputMode="numeric"
                                maxLength={6}
                            />
                            <p className="text-xs text-muted-foreground">Only 6-digit numeric pincodes are allowed.</p>
                        </div>
                        <div className="space-y-1">
                            <Label>Cluster</Label>
                            <Select value={newMappingClusterId} onValueChange={setNewMappingClusterId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select cluster" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(newMappingCityId ? (clustersByCity.get(Number(newMappingCityId)) || []).filter((cluster) => cluster.isActive) : []).map((cluster) => (
                                        <SelectItem key={cluster.id} value={String(cluster.id)}>
                                            {cluster.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center justify-between border rounded-md p-3">
                            <div>
                                <p className="text-sm font-medium">Active mapping</p>
                                <p className="text-xs text-muted-foreground">Inactive mappings are ignored during apply.</p>
                            </div>
                            <Switch checked={newMappingIsActive} onCheckedChange={setNewMappingIsActive} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAddMappingDialogOpen(false)} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button onClick={() => void handleAddMapping()} disabled={!isAdmin || isSaving}>
                            <Save className="h-4 w-4 mr-2" />
                            Save Mapping
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={applyDialog.open} onOpenChange={(open) => setApplyDialog((prev) => ({ ...prev, open }))}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {applyDialog.scope === 'global' ? 'Confirm Global Apply' : 'Confirm City Apply'}
                        </DialogTitle>
                        <DialogDescription>
                            {applyDialog.scope === 'global'
                                ? 'This updates all matched active stores across all cities.'
                                : 'This updates matched active stores for the selected city only.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-md border p-3 text-sm space-y-1">
                        <p>Pending updates: <span className="font-semibold">{preview?.actionCounts.pendingUpdates ?? 0}</span></p>
                        <p>Mapped stores: <span className="font-semibold">{preview?.actionCounts.mapped ?? 0}</span></p>
                        <p>Unmapped stores (unchanged): <span className="font-semibold">{preview?.actionCounts.unmapped ?? 0}</span></p>
                    </div>
                    {(preview?.actionCounts.pendingUpdates ?? 0) === 0 && (
                        <p className="text-xs text-muted-foreground">
                            No pending updates in current preview scope. Apply will complete with zero updates.
                        </p>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setApplyDialog({ open: false, scope: 'city' })} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button
                            variant={applyDialog.scope === 'global' ? 'destructive' : 'default'}
                            onClick={() => void handleApplyMappings()}
                            disabled={isSaving || !isAdmin || (preview?.actionCounts.pendingUpdates ?? 0) === 0}
                        >
                            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Apply Now
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
